import clsx from 'clsx'
import * as Checkbox from '@radix-ui/react-checkbox'
import { CheckIcon } from '@heroicons/react/20/solid'
import type { TypeaheadOptionProps } from './types'

/**
 * TypeaheadOption Component
 *
 * A dropdown option row with checkbox indicator and two-line layout (name + secondary text).
 * Follows the design system patterns from Figma.
 *
 * Receives primitives instead of an item object (Principle 2: Pass Only Primitives).
 */
export function TypeaheadOption({ id, label, description, selected, highlighted, onToggle }: TypeaheadOptionProps) {
  return (
    <div
      role="option"
      aria-selected={selected}
      data-highlighted={highlighted ? 'true' : undefined}
      data-testid={`typeahead-option-${id}`}
      onClick={onToggle}
      className={clsx('flex items-center gap-4 h-[54px] p-2 cursor-pointer rounded', 'hover:bg-brand-neutral-100', highlighted && 'bg-brand-neutral-100')}
    >
      {/* Checkbox indicator - 16x16px per Figma */}
      <Checkbox.Root
        checked={selected}
        tabIndex={-1}
        className={clsx(
          'h-4 w-4 shrink-0 rounded flex items-center justify-center',
          selected ? 'bg-brand-500 border-brand-500' : 'bg-white border border-brand-neutral-300'
        )}
      >
        <Checkbox.Indicator>
          <CheckIcon className="h-3 w-3 text-white" />
        </Checkbox.Indicator>
      </Checkbox.Root>

      {/* Item content */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium text-brand-neutral-950 leading-5 tracking-[-0.15px] truncate" title={label}>
          {label}
        </span>
        {description && (
          <span className="text-sm text-brand-neutral-800 leading-5 tracking-[-0.15px] truncate" title={description}>
            {description}
          </span>
        )}
      </div>
    </div>
  )
}
